import { Component, DestroyRef, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, inject } from '@angular/core';
import { NavigationEnd, Router, RouterLink } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { IonIcon } from '@ionic/angular/standalone';
import { addIcons } from 'ionicons';
import {
  albumsOutline,
  cardOutline,
  chatbubbleEllipsesOutline,
  chevronDownOutline,
  colorWandOutline,
  constructOutline,
  copyOutline,
  cubeOutline,
  globeOutline,
  homeOutline,
  languageOutline,
  libraryOutline,
  pinOutline,
  pinSharp,
  peopleOutline,
  pulseOutline,
  shieldCheckmarkOutline,
  sparklesOutline,
  statsChartOutline,
  storefrontOutline,
} from 'ionicons/icons';
import { filter, startWith } from 'rxjs';
import { APP_NAVIGATION_ITEMS, AppNavigationItem } from './app-navigation.config';
import { PlatformRole } from '../../models/platform-access.models';

@Component({
  selector: 'app-sidebar',
  standalone: true,
  imports: [RouterLink, IonIcon],
  templateUrl: './app-sidebar.component.html',
  styleUrls: ['./app-sidebar.component.scss']
})
export class AppSidebarComponent implements OnInit, OnChanges {
  private readonly destroyRef = inject(DestroyRef);

  @Input() collapsed = false;
  @Input() hidden = false;
  @Input() pinnedExpanded = true;
  @Input() compact = false;
  @Input() open = false;
  @Input() userRole: PlatformRole | null = null;
  @Output() closeRequest = new EventEmitter<void>();
  @Output() pinToggleRequest = new EventEmitter<void>();
  @Output() hoverExpandRequest = new EventEmitter<void>();
  @Output() hoverCollapseRequest = new EventEmitter<void>();

  readonly items = APP_NAVIGATION_ITEMS;
  expandedGroupRoutes = new Set<string>();

  constructor(private readonly router: Router) {
    addIcons({
      homeOutline,
      languageOutline,
      globeOutline,
      chevronDownOutline,
      pinOutline,
      pinSharp,
      colorWandOutline,
      albumsOutline,
      cubeOutline,
      constructOutline,
      libraryOutline,
      storefrontOutline,
      copyOutline,
      shieldCheckmarkOutline,
      peopleOutline,
      pulseOutline,
      cardOutline
      ,chatbubbleEllipsesOutline
      ,sparklesOutline
      ,statsChartOutline
    });
  }

  get visibleItems(): AppNavigationItem[] {
    return this.items
      .map((item) => ({
        ...item,
        children: this.getAccessibleChildren(item)
      }))
      .filter((item) => this.canAccessItem(item) && (!item.children || item.children.length > 0 || !this.hasChildren(item)));
  }

  ngOnInit(): void {
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        startWith(null),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        this.syncExpandedGroup();
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['userRole']) {
      this.syncExpandedGroup();
    }
  }

  get labelsVisible(): boolean {
    return this.compact || !this.collapsed;
  }

  get pinButtonLabel(): string {
    return this.pinnedExpanded ? 'Unpin sidebar' : 'Pin sidebar';
  }

  get pinButtonIcon(): string {
    return this.pinnedExpanded ? 'pin-sharp' : 'pin-outline';
  }

  requestHoverExpansion(): void {
    if (!this.compact && !this.hidden && this.collapsed) {
      this.hoverExpandRequest.emit();
    }
  }

  requestHoverCollapse(): void {
    if (!this.compact && !this.hidden) {
      this.hoverCollapseRequest.emit();
    }
  }

  isGroupExpanded(item: AppNavigationItem): boolean {
    if (!item.children?.length || !this.labelsVisible) {
      return false;
    }

    return this.expandedGroupRoutes.has(item.route);
  }

  isItemActive(item: AppNavigationItem): boolean {
    const matchesRoute = this.router.isActive(item.route, {
      paths: item.exact === false ? 'subset' : 'exact',
      queryParams: 'ignored',
      fragment: 'ignored',
      matrixParams: 'ignored'
    });

    if (matchesRoute) {
      return true;
    }

    return item.aliases?.some((alias) =>
      this.router.isActive(alias, {
        paths: item.exact === false ? 'subset' : 'exact',
        queryParams: 'ignored',
        fragment: 'ignored',
        matrixParams: 'ignored'
      })
    ) ?? false;
  }

  isItemOrChildActive(item: AppNavigationItem): boolean {
    if (this.isItemActive(item)) {
      return true;
    }

    return item.children?.some((child) => this.isItemActive(child)) ?? false;
  }

  handleRootItemClick(item: AppNavigationItem): void {
    if (item.children?.length) {
      if (this.expandedGroupRoutes.has(item.route)) {
        this.expandedGroupRoutes.delete(item.route);
      } else {
        this.expandedGroupRoutes.clear();
        this.expandedGroupRoutes.add(item.route);
      }
      return;
    }

    void this.router.navigateByUrl(item.route);

    if (this.compact) {
      this.closeRequest.emit();
    }
  }

  handleChildNavigate(): void {
    if (this.compact) {
      this.closeRequest.emit();
    }
  }

  closeCompactSidebar(): void {
    if (this.compact) {
      this.closeRequest.emit();
    }
  }

  togglePinMode(): void {
    if (this.compact) {
      return;
    }

    this.pinToggleRequest.emit();
  }

  private syncExpandedGroup(): void {
    const activeGroup = this.visibleItems.find((item) => this.isItemOrChildActive(item));

    if (activeGroup?.children?.length) {
      this.expandedGroupRoutes.clear();
      this.expandedGroupRoutes.add(activeGroup.route);
    }
  }

  private canAccessItem(item: AppNavigationItem): boolean {
    if (!item.requiredRoles?.length) {
      return true;
    }

    if (!this.userRole) {
      return false;
    }

    return item.requiredRoles.includes(this.userRole);
  }

  private hasChildren(item: AppNavigationItem): boolean {
    return !!item.children?.length;
  }

  private getAccessibleChildren(item: AppNavigationItem): AppNavigationItem[] | undefined {
    return item.children?.filter((child) => this.canAccessItem(child));
  }
}
